import React, { useState, useEffect } from 'react'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import { apiGet } from '../utils/api'

const TOURNAMENT_STATUS_PRIORITY: Record<string, number> = {
  ACTIVE: 0,
  PLANNED: 1,
  COMPLETED: 2,
  CANCELLED: 3
}

const TOURNAMENT_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Активные',
  PLANNED: 'Запланированные',
  COMPLETED: 'Завершенные',
  CANCELLED: 'Отмененные'
}

interface IncomeArticle {
  article: string
  amount: number
}

interface ExpenseArticle {
  article: string
  amount: number
}

interface ProfitReport {
  incomeArticles: IncomeArticle[]
  expenseArticles: ExpenseArticle[]
  summary: {
    totalIncome: number
    totalExpense: number
    totalProfit: number
  }
}

interface ArticleDocument {
  id: string
  date: string | Date
  type: string
  documentType: string
  documentNumber: string
  description: string
  amount: number
  tournament?: string | null
  player?: string
  supplier?: string
  hours?: number
  ratePerHour?: number
  details?: Array<{
    itemName: string
    quantity: number
    unitPrice: number
    totalPrice: number
  }>
}

const ReportsProfit: React.FC = () => {
  const [accrualReport, setAccrualReport] = useState<ProfitReport | null>(null)
  const [cashReport, setCashReport] = useState<ProfitReport | null>(null)
  const [loadingAccrual, setLoadingAccrual] = useState(false)
  const [loadingCash, setLoadingCash] = useState(false)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [tournament, setTournament] = useState('')
  const [tournaments, setTournaments] = useState<Array<{id: string, name: string, status?: string, endDate?: string}>>([])
  const [selectedArticle, setSelectedArticle] = useState<{type: 'income' | 'expense', article: string, mode: 'accrual' | 'cash'} | null>(null)
  const [articleDocuments, setArticleDocuments] = useState<ArticleDocument[]>([])
  const [loadingDetails, setLoadingDetails] = useState(false)

  // Загружаем сохраненный период при инициализации
  useEffect(() => {
    const savedFrom = localStorage.getItem('profit-report-from')
    const savedTo = localStorage.getItem('profit-report-to')
    if (savedFrom) setDateFrom(savedFrom)
    if (savedTo) setDateTo(savedTo)
  }, [])

  // Загружаем турниры
  useEffect(() => {
    const fetchTournaments = async () => {
      try {
        // Загружаем все турниры (без пагинации)
        const data = await apiGet<any>('/tournaments', { limit: '1000' })
        const loaded = (data.tournaments || []) as Array<{ id: string; name: string; status?: string; endDate?: string }>
        const sorted = [...loaded].sort((a, b) => {
          const statusA = String(a.status || '').toUpperCase()
          const statusB = String(b.status || '').toUpperCase()
          const priorityA = TOURNAMENT_STATUS_PRIORITY[statusA] ?? Number.MAX_SAFE_INTEGER
          const priorityB = TOURNAMENT_STATUS_PRIORITY[statusB] ?? Number.MAX_SAFE_INTEGER
          if (priorityA !== priorityB) return priorityA - priorityB

          const endDateA = a.endDate ? new Date(a.endDate).getTime() : 0
          const endDateB = b.endDate ? new Date(b.endDate).getTime() : 0
          if (endDateA !== endDateB) return endDateB - endDateA

          return String(a.name || '').localeCompare(String(b.name || ''), 'ru')
        })
        setTournaments(sorted)
      } catch (error) {
        console.error('Ошибка загрузки турниров:', error)
      }
    }
    fetchTournaments()
  }, [])

  // Сохраняем период в localStorage
  const savePeriod = (fromDate: string, toDate: string) => {
    if (fromDate) {
      localStorage.setItem('profit-report-from', fromDate)
    } else {
      localStorage.removeItem('profit-report-from')
    }
    if (toDate) {
      localStorage.setItem('profit-report-to', toDate)
    } else {
      localStorage.removeItem('profit-report-to')
    }
  }

  // Сброс фильтров
  const resetFilters = () => {
    setDateFrom('')
    setDateTo('')
    setTournament('')
    localStorage.removeItem('profit-report-from')
    localStorage.removeItem('profit-report-to')
  }

  const fetchData = async (currentMode: 'accrual' | 'cash') => {
    if (currentMode === 'accrual') {
      setLoadingAccrual(true)
    } else {
      setLoadingCash(true)
    }
    
    try {
      const params: Record<string, string> = {}
      if (dateFrom) params.from = dateFrom
      if (dateTo) params.to = dateTo
      if (tournament) params.tournament = tournament
      params.mode = currentMode
      
      const data = await apiGet<ProfitReport>('/reports/profit', params)
      
      if (currentMode === 'accrual') {
        setAccrualReport(data)
      } else {
        setCashReport(data)
      }
      
      // Сохраняем период
      if (dateFrom || dateTo) {
        savePeriod(dateFrom, dateTo)
      }
    } catch (error) {
      console.error(`Ошибка загрузки отчета (${currentMode}):`, error)
      if (currentMode === 'accrual') {
        setAccrualReport(null)
      } else {
        setCashReport(null)
      }
    } finally {
      if (currentMode === 'accrual') {
        setLoadingAccrual(false)
      } else {
        setLoadingCash(false)
      }
    }
  }

  useEffect(() => {
    fetchData('accrual')
    fetchData('cash')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, tournament])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'MDL',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const handleArticleClick = async (type: 'income' | 'expense', article: string, mode: 'accrual' | 'cash') => {
    setSelectedArticle({ type, article, mode })
    setLoadingDetails(true)
    
    try {
      const params: Record<string, string> = {
        article: encodeURIComponent(article)
      }
      if (dateFrom) params.from = dateFrom
      if (dateTo) params.to = dateTo
      if (tournament) params.tournament = tournament
      params.mode = mode
      
      const data = await apiGet<{ documents: ArticleDocument[] }>(`/reports/profit/${type}/${encodeURIComponent(article)}/details`, params)
      setArticleDocuments(data.documents || [])
    } catch (error) {
      console.error('Ошибка загрузки детализации:', error)
      setArticleDocuments([])
    } finally {
      setLoadingDetails(false)
    }
  }

  const closeModal = () => {
    setSelectedArticle(null)
    setArticleDocuments([])
  }

  const groupedTournaments = tournaments.reduce<Record<string, Array<{id: string, name: string, status?: string, endDate?: string}>>>((acc, item) => {
    const status = String(item.status || '').toUpperCase() || 'UNKNOWN'
    if (!acc[status]) acc[status] = []
    acc[status].push(item)
    return acc
  }, {})

  const orderedTournamentStatuses = Object.keys(groupedTournaments).sort((a, b) => {
    const pa = TOURNAMENT_STATUS_PRIORITY[a] ?? Number.MAX_SAFE_INTEGER
    const pb = TOURNAMENT_STATUS_PRIORITY[b] ?? Number.MAX_SAFE_INTEGER
    return pa - pb
  })

  return (
    <div className="p-2">
      <div className="mb-2">
        <h1 className="text-xl font-bold text-gray-900 mb-2">
          💰 Прибыль
        </h1>
        
        {/* Фильтры */}
        <div className="flex gap-3 mb-2 flex-wrap">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              От даты
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              До даты
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Турнир
            </label>
            <select
              value={tournament}
              onChange={(e) => setTournament(e.target.value)}
              className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Все турниры</option>
              <option value="__OTHER__">Прочие</option>
              {orderedTournamentStatuses.map((status) => (
                <optgroup
                  key={status}
                  label={`──────── ${TOURNAMENT_STATUS_LABELS[status] || status} ────────`}
                >
                  {groupedTournaments[status].map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={() => {
                fetchData('accrual')
                fetchData('cash')
              }}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              Обновить
            </button>
            <button
              onClick={resetFilters}
              className="px-3 py-1 text-sm bg-red-100 text-red-800 rounded hover:bg-red-200 focus:outline-none focus:ring-1 focus:ring-red-500"
            >
              Сбросить
            </button>
          </div>
        </div>

      </div>

      {/* Два блока таблиц рядом */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        {/* Левая часть - Режим начисления */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Режим: Начисление</h2>
          
          {/* Прибыль */}
          {accrualReport && accrualReport.summary && (
            <div className="bg-white rounded shadow overflow-hidden mb-2">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <tbody className="bg-white">
                    <tr className="bg-blue-50">
                      <td className="px-2 py-2 text-lg font-semibold text-gray-900">
                        Прибыль
                      </td>
                      <td className={`px-2 py-2 text-right font-bold text-lg ${accrualReport.summary.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(accrualReport.summary.totalProfit)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Доходы */}
          <div className="bg-white rounded shadow overflow-hidden mb-2">
            <div className="bg-green-50 px-3 py-1 border-b">
              <h2 className="text-lg font-semibold text-green-800">Доходы</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">
                      Статья
                    </th>
                    <th className="px-2 py-1 text-right text-xs font-medium text-gray-500 uppercase">
                      Сумма
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loadingAccrual ? (
                    <tr>
                      <td colSpan={2} className="px-2 py-2 text-center text-gray-500 text-sm">
                        Загрузка...
                      </td>
                    </tr>
                  ) : accrualReport && accrualReport.incomeArticles.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="px-2 py-2 text-center text-gray-500 text-sm">
                        Нет данных
                      </td>
                    </tr>
                  ) : (
                    <>
                      {accrualReport?.incomeArticles.map((item, index) => (
                        <tr 
                          key={index} 
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleArticleClick('income', item.article, 'accrual')}
                        >
                          <td className="px-2 py-1 text-sm text-gray-900">
                            {item.article}
                          </td>
                          <td className={`px-2 py-1 text-sm text-right font-medium ${item.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(item.amount)}
                          </td>
                        </tr>
                      ))}
                      {accrualReport && accrualReport.summary && (
                        <tr className="bg-gray-50 font-semibold">
                          <td className="px-2 py-1 text-sm text-gray-900">
                            Итого
                          </td>
                          <td className={`px-2 py-1 text-sm text-right ${accrualReport.summary.totalIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(accrualReport.summary.totalIncome)}
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Расходы */}
          <div className="bg-white rounded shadow overflow-hidden">
            <div className="bg-red-50 px-3 py-1 border-b">
              <h2 className="text-lg font-semibold text-red-800">Расходы</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">
                      Статья
                    </th>
                    <th className="px-2 py-1 text-right text-xs font-medium text-gray-500 uppercase">
                      Сумма
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loadingAccrual ? (
                    <tr>
                      <td colSpan={2} className="px-2 py-2 text-center text-gray-500 text-sm">
                        Загрузка...
                      </td>
                    </tr>
                  ) : accrualReport && accrualReport.expenseArticles.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="px-2 py-2 text-center text-gray-500 text-sm">
                        Нет данных
                      </td>
                    </tr>
                  ) : (
                    <>
                      {accrualReport?.expenseArticles.map((item, index) => (
                        <tr 
                          key={index} 
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleArticleClick('expense', item.article, 'accrual')}
                        >
                          <td className="px-2 py-1 text-sm text-gray-900">
                            {item.article}
                          </td>
                          <td className="px-2 py-1 text-sm text-right font-medium text-red-600">
                            {formatCurrency(item.amount)}
                          </td>
                        </tr>
                      ))}
                      {accrualReport && accrualReport.summary && (
                        <tr className="bg-gray-50 font-semibold">
                          <td className="px-2 py-1 text-sm text-gray-900">
                            Итого
                          </td>
                          <td className="px-2 py-1 text-sm text-right text-red-600">
                            {formatCurrency(accrualReport.summary.totalExpense)}
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Правая часть - Кассовый режим */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Режим: Кассовый</h2>
          
          {/* Прибыль */}
          {cashReport && cashReport.summary && (
            <div className="bg-white rounded shadow overflow-hidden mb-2">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <tbody className="bg-white">
                    <tr className="bg-blue-50">
                      <td className="px-2 py-2 text-lg font-semibold text-gray-900">
                        Прибыль
                      </td>
                      <td className={`px-2 py-2 text-right font-bold text-lg ${cashReport.summary.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(cashReport.summary.totalProfit)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Доходы */}
          <div className="bg-white rounded shadow overflow-hidden mb-2">
            <div className="bg-green-50 px-3 py-1 border-b">
              <h2 className="text-lg font-semibold text-green-800">Доходы</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">
                      Статья
                    </th>
                    <th className="px-2 py-1 text-right text-xs font-medium text-gray-500 uppercase">
                      Сумма
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loadingCash ? (
                    <tr>
                      <td colSpan={2} className="px-2 py-2 text-center text-gray-500 text-sm">
                        Загрузка...
                      </td>
                    </tr>
                  ) : cashReport && cashReport.incomeArticles.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="px-2 py-2 text-center text-gray-500 text-sm">
                        Нет данных
                      </td>
                    </tr>
                  ) : (
                    <>
                      {cashReport?.incomeArticles.map((item, index) => (
                        <tr 
                          key={index} 
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleArticleClick('income', item.article, 'cash')}
                        >
                          <td className="px-2 py-1 text-sm text-gray-900">
                            {item.article}
                          </td>
                          <td className={`px-2 py-1 text-sm text-right font-medium ${item.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(item.amount)}
                          </td>
                        </tr>
                      ))}
                      {cashReport && cashReport.summary && (
                        <tr className="bg-gray-50 font-semibold">
                          <td className="px-2 py-1 text-sm text-gray-900">
                            Итого
                          </td>
                          <td className={`px-2 py-1 text-sm text-right ${cashReport.summary.totalIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(cashReport.summary.totalIncome)}
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Расходы */}
          <div className="bg-white rounded shadow overflow-hidden">
            <div className="bg-red-50 px-3 py-1 border-b">
              <h2 className="text-lg font-semibold text-red-800">Расходы</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">
                      Статья
                    </th>
                    <th className="px-2 py-1 text-right text-xs font-medium text-gray-500 uppercase">
                      Сумма
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loadingCash ? (
                    <tr>
                      <td colSpan={2} className="px-2 py-2 text-center text-gray-500 text-sm">
                        Загрузка...
                      </td>
                    </tr>
                  ) : cashReport && cashReport.expenseArticles.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="px-2 py-2 text-center text-gray-500 text-sm">
                        Нет данных
                      </td>
                    </tr>
                  ) : (
                    <>
                      {cashReport?.expenseArticles.map((item, index) => (
                        <tr 
                          key={index} 
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleArticleClick('expense', item.article, 'cash')}
                        >
                          <td className="px-2 py-1 text-sm text-gray-900">
                            {item.article}
                          </td>
                          <td className="px-2 py-1 text-sm text-right font-medium text-red-600">
                            {formatCurrency(item.amount)}
                          </td>
                        </tr>
                      ))}
                      {cashReport && cashReport.summary && (
                        <tr className="bg-gray-50 font-semibold">
                          <td className="px-2 py-1 text-sm text-gray-900">
                            Итого
                          </td>
                          <td className="px-2 py-1 text-sm text-right text-red-600">
                            {formatCurrency(cashReport.summary.totalExpense)}
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Модальное окно с детализацией */}
      {selectedArticle && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white max-h-[85vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Детализация: {selectedArticle.article} ({selectedArticle.mode === 'accrual' ? 'Начисление' : 'Кассовый'})
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Закрыть</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {loadingDetails ? (
                <div className="text-center py-4 text-gray-500">Загрузка...</div>
              ) : articleDocuments.length === 0 ? (
                <div className="text-center py-4 text-gray-500">Нет документов</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Дата
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Тип
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Номер
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Описание
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Турнир
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          Сумма
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {articleDocuments.map((doc) => (
                        <tr key={doc.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">
                            {format(new Date(doc.date), 'dd.MM.yyyy', { locale: ru })}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900">
                            {doc.documentType}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 font-mono">
                            {doc.documentNumber}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900">
                            <div>{doc.description}</div>
                            {doc.player && (
                              <div className="text-xs text-gray-500">Игрок: {doc.player}</div>
                            )}
                            {doc.supplier && (
                              <div className="text-xs text-gray-500">Поставщик: {doc.supplier}</div>
                            )}
                            {doc.hours && doc.ratePerHour && (
                              <div className="text-xs text-gray-500">
                                {doc.hours} ч. × {formatCurrency(doc.ratePerHour)}/ч
                              </div>
                            )}
                            {doc.details && doc.details.length > 0 && (
                              <details className="mt-1">
                                <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-800">
                                  Детализация ({doc.details.length})
                                </summary>
                                <div className="mt-2 ml-4 border-l-2 border-gray-200 pl-2 space-y-3">
                                  {doc.details.map((detail: any, idx: number) => {
                                    // Проверяем, есть ли поля revenue, cost, margin (для маржи)
                                    const isMarginBreakdown = detail.revenue !== undefined && detail.cost !== undefined && detail.margin !== undefined
                                    
                                    if (isMarginBreakdown) {
                                      // Детализация маржи: показываем 3 строки для каждой позиции
                                      return (
                                        <div key={idx} className="mb-3 border-b border-gray-200 pb-2 last:border-b-0 last:pb-0">
                                          <div className="font-medium text-xs text-gray-700 mb-1">{detail.itemName}</div>
                                          <div className="ml-2 space-y-1 text-xs">
                                            <div className="flex justify-between">
                                              <span className="text-gray-600">Выручка:</span>
                                              <span className="text-green-600 font-medium">+{formatCurrency(detail.revenue)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                              <span className="text-gray-600">Стоимость:</span>
                                              <span className="text-red-600 font-medium">-{formatCurrency(detail.cost)}</span>
                                            </div>
                                            <div className="flex justify-between pt-1 border-t border-gray-100">
                                              <span className="text-gray-700 font-medium">Маржа:</span>
                                              <span className={`font-medium ${detail.margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                {detail.margin >= 0 ? '+' : ''}{formatCurrency(detail.margin)}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      )
                                    } else {
                                      // Обычная детализация (для других документов)
                                      return (
                                        <div key={idx} className="mb-1">
                                          <div className="flex justify-between text-xs">
                                            <span>{detail.itemName}</span>
                                            <span className="font-medium">{formatCurrency(detail.totalPrice || detail.unitPrice)}</span>
                                          </div>
                                          {(detail.quantity || detail.unitPrice) && (
                                            <div className="text-xs text-gray-500 ml-2">
                                              {detail.quantity && `${detail.quantity} × `}
                                              {detail.unitPrice && formatCurrency(detail.unitPrice)}
                                            </div>
                                          )}
                                        </div>
                                      )
                                    }
                                  })}
                                </div>
                              </details>
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900">
                            {doc.tournament || '-'}
                          </td>
                          <td className={`px-3 py-2 text-sm text-right font-medium ${doc.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {doc.amount < 0 ? '-' : ''}{formatCurrency(Math.abs(doc.amount))}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50 font-semibold">
                        <td colSpan={5} className="px-3 py-2 text-sm text-gray-900 text-right">
                          Итого:
                        </td>
                        <td className={`px-3 py-2 text-sm text-right ${articleDocuments.reduce((sum, d) => sum + d.amount, 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {(() => {
                            const total = articleDocuments.reduce((sum, d) => sum + d.amount, 0)
                            return (total < 0 ? '-' : '') + formatCurrency(Math.abs(total))
                          })()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            
            <div className="mt-4 flex justify-end border-t pt-4">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ReportsProfit

